import React, { Fragment, useEffect, useRef, useState } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import "./style.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState, store, useTypedSelector } from "redux/store";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useFormik } from "formik";
import { Plus, X } from "lucide-react";
import * as yup from "yup";
import {
  ClassesListAsync,
  DisplayClassesDataList,
} from "redux/features/classesSlice";
import {
  clearSubjectList,
  DisplaySubjectDataList,
  SubjectListAsync,
} from "redux/features/subjectSlice";
import {
  clearTopicList,
  DisplayTopicDataList,
  TopicListAsync,
} from "redux/features/topicSlice";
import {
  CreateQuestionAsync,
  QuestionListAsync,
  selectSimilarQuestionDataList,
  similarQuestionAsync,
  updateQuestionAsync,
  updateQuestionStatusAsync,
} from "redux/features/questionSlice";
import { CreateQuestionBatchAsync } from "redux/features/questionSlice";
import { Question } from "types/schoolAdmin/questionTypes";
import { useDispatch } from "react-redux";
import {
  ChapterListAsync,
  clearChapterList,
  DisplayChapterDataList,
} from "redux/features/chaptersSlice";
import { CKEditor } from "ckeditor4-react";
import { Form, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Spinner } from "react-bootstrap";
import { toastError } from "helpers/toastHelper";
import LoadingCard from "components/common/LoadingCard";
import { Loadable, Loader } from "components";

// Static values here //
const skills = [{ name: "Low" }, { name: "Medium" }, { name: "High" }];

const occurringHistory = [
  { name: "-- None --" },
  { name: "Board Exams" },
  { name: "Competitive Exams" },
];

const BoardExams = [{ name: "-- None --" }, { name: "CBSE" }, { name: "ICSE" }];

interface Year {
  name: string;
}

const startYear = 1980;
const endYear = 2050;

const BoardExamsYear: Year[] = [{ name: "-- None --" }];

for (let year = startYear; year <= endYear; year++) {
  BoardExamsYear.push({ name: year.toString() });
}

const CompetitiveExamsYear: Year[] = [{ name: "-- None --" }];

for (let year = startYear; year <= endYear; year++) {
  CompetitiveExamsYear.push({ name: year.toString() });
}

const questionType = [
  { name: "Multiple-choice" },
  { name: "True / False" },
  { name: "Match Making" },
  { name: "Very Short Answer" },
  { name: "Short answer" },
  { name: "Long" },
  { name: "Paragraph" },
];

const questionLevel = [
  { name: "Easy" },
  { name: "Medium" },
  { name: "Difficult" },
  { name: "HOTS" },
];

const connectedWith = [{ name: "Chapters" }, { name: "Topics" }];

const BasedOn = [
  { name: "-- None --" },
  { name: "Graph" },
  { name: "Map Work" },
  { name: "Data Analysis" },
  { name: "Interpretation" },
  { name: "Case Study" },
  { name: "Symbolism" },
  { name: "Central Idea" },
  { name: "Mood" },
  { name: "Tone" },
  { name: "Themes" },
  { name: "Poetic device" },
  { name: "Structure" },
  { name: "Title" },
  { name: "Contrast" },
];

const ConceptLevel = [
  { name: "Remembering" },
  { name: "Understanding" },
  { name: "Application" },
  { name: "Analyzing" },
  { name: "Evaluating" },
  { name: "Creating" },
];

const questionMarks = [
  { name: "0.5" },
  { name: "1" },
  { name: "1.5" },
  { name: "2" },
  { name: "2.5" },
  { name: "3" },
  { name: "3.5" },
  { name: "4" },
  { name: "4.5" },
  { name: "5" },
  { name: "5.5" },
  { name: "6" },
  { name: "6.5" },
  { name: "7" },
  { name: "7.5" },
  { name: "8" },
  { name: "8.5" },
  { name: "9" },
  { name: "9.5" },
  { name: "10" },
  { name: "10.5" },
  { name: "11" },
  { name: "11.5" },
  { name: "12" },
];

// validations message here //
const validationSchema = yup.object().shape({
  question_text: yup.string().required("Please enter question text"),
  grade_id: yup
    .array()
    .min(1, "Please select class")
    .required("Please select class"),
  subject_id: yup
    .array()
    .min(1, "Please select subject")
    .required("Please select subject"),
  type: yup.string().required("Please select question type"),
  level: yup.string().required("Please select level"),
  connected_with: yup.string().required("Please select connected With"),
  chapter_id: yup
    .array()
    .min(1, "Please select chapter")
    .required("Please select chapter"),
  topic_id: yup
    .array()
    .min(1, "Please select topic")
    .required("Please select topic"),
  max_marks: yup.number().required("Please select marks"),
  concept_level: yup.string().required("Please select concept level"),
  skill: yup.string().required("Please select skill"),
});

declare global {
  interface Window {
    katex: typeof katex;
  }
}
interface CreateQuestionsProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  isModal?: boolean;
}
// create Questions components here //
const CreateQuestions: React.FC<CreateQuestionsProps> = ({
  onSuccess,
  onCancel,
  isModal = false
}) => {
  let dispatch = useDispatch<typeof store.dispatch>();
  const [statusInfo, setStatusInfo] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [show, setShow] = useState(false);
  const [pendingParentChange, setPendingParentChange] = useState({
    subject: false,
    chapter: false,
  });
  const [pendingReset, setPendingReset] = useState({
    subject: false,
    chapter: false,
    topic: false,
  });
  const [isLoadingUpdateData, setIsLoadingUpdateData] = useState(false);

  // List Data here //
  const classesDataList = useTypedSelector(DisplayClassesDataList);
  const subjectDataList = useTypedSelector(DisplaySubjectDataList);
  const TopicDataList = useTypedSelector(DisplayTopicDataList);
  const { chaptersDataList }: any = useTypedSelector(DisplayChapterDataList);

  const handleClose = () => {
    setShow(false);
  };

  const isLoading = useSelector(
    (state: RootState) => state.createQuestionState.isLoading
  );

  const location = useLocation();
  const isUpdating = !!location?.state?.question;
  const question = location?.state?.question || {};

  const getClassId = () =>
    classesDataList.find((cls) => cls.title === question?.grades?.[0])?.id ||
    "";

  const getSubjectId = () =>
    subjectDataList.find((subj) => subj.title === question?.subjects?.[0])
      ?.id || "";

  const getChapterId = () =>
    chaptersDataList.find((ch) => ch.chapter === question?.chapters?.[0])?.id ||
    "";

  const getTopicId = () =>
    TopicDataList.find((top) => top.title === question?.topics?.[0])?.id || "";

  const initialValuesRef = useRef<Question.CreateQuestionType | null>(null);

  // initial values here //
  if (!initialValuesRef.current) {
    const initialValues: Question.CreateQuestionType = {
      question_text: question?.question_text || "",
      answer_text: question?.answer_text || "",
      grade_id: isUpdating && getClassId() ? [getClassId()] : [],
      subject_id: isUpdating && getSubjectId() ? [getSubjectId()] : [],
      chapter_id: isUpdating && getChapterId() ? [getChapterId()] : [],
      topic_id: isUpdating && getTopicId() ? [getTopicId()] : [],
      type: question?.type || "",
      level: question?.level || "",
      connected_with: question?.connected_with || "",
      based_on: question?.based_on || "",
      concept_level: question?.concept_level || "",
      skill: question?.skill || "",
      max_marks: question?.max_marks || null,
      occuring_history: question?.occuring_history || "",
      board_exam: question?.board_exam || "",
      board_exam_year: question?.board_exam_year || "",
      competative_exam: question?.competative_exam || "",
      competative_exam_year: question?.competative_exam_year || "",
      reference_by: question?.reference_by || "",
      id: question?.id || undefined,
      status: question?.status,
    };
    initialValuesRef.current = initialValues;
  }

  const [validState, setValidState] = useState<Question.CreateQuestionType>(
    initialValuesRef.current
  );

  const [editorQuestionHtml, setEditorQuestionHtml] = useState(
    initialValuesRef.current?.question_text
  );

  const navigate = useNavigate();

  const [editorAnswerHtml, setEditorAnswerHtml] = useState(
    initialValuesRef.current?.answer_text
  );
  const [gradeId, setGradeId] = useState<any>([]);

  // status change logic here //
  const handleStatusChangeInUser = async (e) => {
    const value = e.target.checked;
    setStatusInfo(value);
    await dispatch(updateQuestionStatusAsync({ id: values.id }));
    dispatch(
      QuestionListAsync({
        statusId: true,
        isAllDataFetch: undefined,
      })
    );
  };

  useEffect(() => {
    setStatusInfo(question?.status);
  }, [question]);

  useEffect(() => {
    window.katex = katex;
    if (isUpdating && initialValuesRef.current?.answer_text) {
      setShowAnswerText(true);
    }
  }, [isUpdating, initialValuesRef.current?.answer_text]);

  // Formik and submit logic here //

  const formik = useFormik({
    // initialValues,
    initialValues: initialValuesRef.current,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const selectedClassId = classesDataList.find(
        (classItem) => classItem?.title === values?.grade_id[0]
      )?.id;

      const selectedSubjectId = subjectDataList.find(
        (subject) => subject?.title === values?.subject_id[0]
      )?.id;

      const selectedTopicId = TopicDataList.find(
        (topic) => topic?.title === values?.topic_id[0]
      )?.id;

      const selectedChapterId = chaptersDataList?.find(
        (chapter) => chapter?.chapter === values?.chapter_id[0]
      )?.id;

      const payload = {
        ...values,
        grade_id: selectedClassId ? [selectedClassId] : values?.grade_id,
        subject_id: selectedSubjectId
          ? [selectedSubjectId]
          : values?.subject_id,
        topic_id: selectedTopicId ? [selectedTopicId] : values?.topic_id,
        chapter_id: selectedChapterId
          ? [selectedChapterId]
          : values?.chapter_id,
        question_options:
          formik.values.type === "Multiple-choice" ||
            formik.values.type === "True / False"
            ? optionEditors?.map((option, index) => ({
              data: option,
              option: index + 1,
            }))
            : [],
        answer_text:
          formik.values.type === "Multiple-choice" ||
            formik.values.type === "True / False"
            ? selectedAnswers.join(", ")
            : values?.answer_text,
      };

      let response;
      if (isUpdating) {
        const { id, status, ...updatePayload } = payload;
        response = await dispatch(
          updateQuestionAsync({
            id: values?.id,
            questionDetails: updatePayload,
          })
        );
      } else {
        const { status, ...createPayload } = payload;
        response = await dispatch(CreateQuestionAsync(createPayload));
      }

      if (response?.meta?.requestStatus === "fulfilled") {
        resetForm();
        setShow(false);
        if (isModal && onSuccess) {
          onSuccess();
        }
        // If this component is used as a standalone page (e.g. /questions/create),
        // navigate back to questions listing after a successful create/update.
        if (!isModal) {
          navigate("/school-admin/questions");
        }
      } else {
        handleClose();
      }
    },
    enableReinitialize: true,
  });

  const { values, handleChange, errors, touched } = formik;

  const selectedClass = classesDataList.find(
    (item) => item.id === Number(formik.values.grade_id[0])
  );
  const selectedSubject = subjectDataList.find(
    (item) => item.id === Number(formik.values.subject_id[0])
  );
  const selectedChapter = chaptersDataList.find(
    (item) => item.id === Number(formik.values.chapter_id[0])
  );
  const selectedTopic = TopicDataList.find(
    (item) => item.id === Number(formik.values.topic_id[0])
  );

  // question Editor logic here //
  const handleQuestionEditor = (event) => {
    const value = event.editor?.getData();
    setEditorQuestionHtml(value);
    handleFieldChange("question_text", value);
  };

  // Answer Editor logic here //
  const handleAnswerEditor = (event) => {
    const value = event.editor?.getData();
    setEditorAnswerHtml(value);
    handleFieldChange("answer_text", value);
  };

  const handleFieldChange = (
    field: keyof Question.CreateQuestionType,
    value: any
  ) => {
    let selectedValue: any;

    if (field === "max_marks") {
      selectedValue = value ? Number(value.name) : null;

      // Validate that new max_marks is not less than sum of sub-question marks
      if (selectedValue !== null && subMarkBlocks.length > 0) {
        const totalSubMarks = subMarkBlocks.reduce((sum, b) => sum + (Number(b.marks) || 0), 0);
        if (totalSubMarks > selectedValue) {
          setSubMarkError(
            `Cannot set max marks to ${selectedValue}. Total sub-question marks (${totalSubMarks}) exceeds this value. Please adjust sub-question marks first.`
          );
          toastError(
            `Cannot update max marks to ${selectedValue}. Total sub-question marks is ${totalSubMarks}.`
          );
          return;
        }
      }
      setSubMarkError("");
    } else if (field === "question_text" || field === "answer_text" || field === "reference_by") {
      selectedValue = value;
    } else {
      selectedValue = value?.name;
      // Handle "-- None --" as null for payload
      if (selectedValue === "-- None --") {
        selectedValue = null;
      }
    }

    setValidState((prevState) => ({
      ...prevState,
      [field]: selectedValue,
    }));

    handleChange({ target: { name: field, value: selectedValue } });
  };

  // checkbox logic here //
  const handleCheckboxChange = (index) => {
    setSelectedAnswers((prev) => {
      const newAnswers = [...prev];
      if (newAnswers.includes(index + 1)) {
        return newAnswers.filter((id) => id !== index + 1);
      } else {
        return [...newAnswers, index + 1];
      }
    });
  };

  const onSubmit = () => {
    formik.setSubmitting(true);
    if (extraQuestionBlocks.length > 0 || subMarkBlocks.length > 0) {
      const selectedClassId = classesDataList.find(
        (classItem) => classItem?.title === formik.values?.grade_id[0]
      )?.id;
      const selectedSubjectId = subjectDataList.find(
        (subject) => subject?.title === formik.values?.subject_id[0]
      )?.id;
      const selectedTopicId = TopicDataList.find(
        (topic) => topic?.title === formik.values?.topic_id[0]
      )?.id;
      const selectedChapterId = chaptersDataList?.find(
        (chapter) => chapter?.chapter === formik.values?.chapter_id[0]
      )?.id;

      const baseShared: any = {
        grade_id: selectedClassId ? [selectedClassId] : formik.values?.grade_id,
        subject_id: selectedSubjectId
          ? [selectedSubjectId]
          : formik.values?.subject_id,
        topic_id: selectedTopicId ? [selectedTopicId] : formik.values?.topic_id,
        chapter_id: selectedChapterId
          ? [selectedChapterId]
          : formik.values?.chapter_id,
        type: formik.values.type,
        level: formik.values.level,
        connected_with: formik.values.connected_with,
        based_on: formik.values.based_on,
        concept_level: formik.values.concept_level,
        skill: formik.values.skill,
        max_marks: formik.values.max_marks,
        occuring_history: formik.values.occuring_history,
        board_exam: formik.values.board_exam,
        board_exam_year: formik.values.board_exam_year,
        competative_exam: formik.values.competative_exam,
        competative_exam_year: formik.values.competative_exam_year,
        reference_by: formik.values.reference_by,
      };
      // If sub-mark questions are present, mark all batch items accordingly
      const sharedForBatch: any =
        subMarkBlocks.length > 0
          ? { ...baseShared, is_sub_marking: true }
          : baseShared;

      const firstPayload = {
        ...sharedForBatch,
        question_text: editorQuestionHtml,
        question_options:
          formik.values.type === "Multiple-choice" ||
            formik.values.type === "True / False"
            ? optionEditors?.map((opt, idx) => ({ data: opt, option: idx + 1 }))
            : [],
        answer_text:
          formik.values.type === "Multiple-choice" ||
            formik.values.type === "True / False"
            ? selectedAnswers.join(", ")
            : editorAnswerHtml,
      };

      const extrasPayload = extraQuestionBlocks.map((b) => ({
        ...sharedForBatch,
        question_text: b.questionHtml,
        question_options:
          formik.values.type === "Multiple-choice" ||
            formik.values.type === "True / False"
            ? b.optionEditors.map((opt, idx) => ({
              data: opt,
              option: idx + 1,
            }))
            : [],
        answer_text:
          formik.values.type === "Multiple-choice" ||
            formik.values.type === "True / False"
            ? b.selectedAnswers.join(", ")
            : b.answerHtml,
      }));

      const subMarksPayload = subMarkBlocks.map((b) => {
        const effectiveType =
          b.type && b.type.trim().length > 0 ? b.type : sharedForBatch.type;

        const isMCQOrTF =
          effectiveType === "Multiple-choice" || effectiveType === "True / False";

        return {
          ...sharedForBatch,
          type: effectiveType,
          max_marks: b.marks,
          question_text: b.questionHtml,
          question_options: isMCQOrTF
            ? b.optionEditors.map((opt, idx) => ({
              data: opt,
              option: idx + 1,
            }))
            : [],
          answer_text: isMCQOrTF
            ? b.selectedAnswers.join(", ")
            : b.answerHtml,
        };
      });

      // Validate sub-question marks before submission
      if (subMarkBlocks.length > 0) {
        const totalSubMarks = subMarkBlocks.reduce((sum, b) => sum + (Number(b.marks) || 0), 0);
        const maxMarks = Number(formik.values.max_marks) || 0;

        if (totalSubMarks > maxMarks) {
          setSubMarkError(
            `Cannot submit. Total sub-question marks (${totalSubMarks}) exceeds max marks (${maxMarks}).`
          );
          toastError(
            `Cannot submit. Total sub-question marks (${totalSubMarks}) exceeds max marks (${maxMarks}).`
          );
          formik.setSubmitting(false);
          return;
        }
      }

      // Build batch payload. In edit mode, include question_id to update existing questions
      let batch = [firstPayload, ...extrasPayload, ...subMarksPayload];

      if (isUpdating) {
        let withIds = batch.map((item, index) => {
          // For the main/root question use the current values.id
          if (index === 0) {
            return { ...item, question_id: values?.id };
          }

          // For extra/sub-mark blocks, respect an existing question_id on the block if present
          // Expectation: blocks may optionally include question_id when editing existing sub-questions
          const block =
            index - 1 < extraQuestionBlocks.length
              ? extraQuestionBlocks[index - 1]
              : subMarkBlocks[index - 1 - extraQuestionBlocks.length];

          const existingId =
            (block && (block.question_id || block.id)) || undefined;
          return existingId ? { ...item, question_id: existingId } : item;
        });

        // Ensure is_sub_marking flag is present when editing sub-mark blocks
        if (subMarkBlocks.length > 0) {
          withIds = withIds.map((i) => ({ ...i, is_sub_marking: true }));
        }

        batch = withIds;
      }

      dispatch(CreateQuestionBatchAsync(batch))
        .then((action: any) => {
          if (action?.meta?.requestStatus === "fulfilled") {
            setShow(false);
            navigate("/school-admin/questions");
          }
        })
        .finally(() => {
          formik.setSubmitting(false);
        });
    } else {
      formik.submitForm();
    }
  };

  useEffect(() => {
    if (!isLoading) {
      formik.setSubmitting(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (classesDataList.length > 0) {
      const selectedClassId = classesDataList.find(
        (item: any) => item?.title === values?.grade_id[0]
      )?.id;
      if (selectedClassId) {
        setGradeId([selectedClassId]);
      }
    }
  }, [classesDataList, values]);

  useEffect(() => {
    dispatch(ClassesListAsync({}));
    // dispatch(
    //   QuestionListAsync({
    //     isAllDataFetch: undefined,
    //   })
    // );
  }, [dispatch]);

  useEffect(() => {
    if (
      !isUpdating &&
      formik.values.grade_id[0] &&
      formik.values.subject_id[0]
    ) {
      dispatch(
        ChapterListAsync({
          gradeId: [formik.values.grade_id[0]],
          subjectId: [formik.values.subject_id[0]],
        })
      );
      formik.setFieldValue("chapter_id", []);
      formik.setFieldValue("topic_id", []);
    } else if (!isUpdating) {
      dispatch(clearChapterList());
      dispatch(clearTopicList());
    }
  }, [formik.values.subject_id[0], formik.values.grade_id[0]]);

  useEffect(() => {
    if (
      formik.values.grade_id[0] &&
      formik.values.subject_id[0] &&
      formik.values.chapter_id[0]
    ) {
      dispatch(
        TopicListAsync({
          chapterId: [formik.values.chapter_id[0]],
          gradeId: [formik.values.grade_id[0]],
          subjectId: [formik.values.subject_id[0]],
        })
      );
      // formik.setFieldValue('topic_id', []);
    } else {
      dispatch(clearTopicList());
    }
  }, [
    formik.values.chapter_id[0],
    formik.values.subject_id[0],
    formik.values.grade_id[0],
  ]);

  const handleCancelBtn = () => {
    if (isModal && onCancel) {
      onCancel();
    } else {
      navigate("/school-admin/questions");
    }
  };

  const [showAnswerText, setShowAnswerText] = useState(false);
  const [optionEditors, setOptionEditors] = useState<string[]>([]);
  const [extraQuestionBlocks, setExtraQuestionBlocks] = useState<any[]>([]);
  const [subMarkBlocks, setSubMarkBlocks] = useState<any[]>([]);
  const [subMarkError, setSubMarkError] = useState<string>("");

  const createEmptyQuestionBlock = () => ({
    questionHtml: "",
    answerHtml: "",
    optionEditors: [],
    selectedAnswers: [],
  });

  const createEmptySubMarkBlock = () => ({
    questionHtml: "",
    answerHtml: "",
    optionEditors: [],
    selectedAnswers: [],
    marks: null as number | null,
    type: "" as string,
  });

  const handleOpenEditor = () => {
    setOptionEditors((prevOptions) => [...prevOptions, ""]);
  };

  // Pre-fill sub-mark questions in edit mode
  useEffect(() => {
    if (!isUpdating) return;
    const subs: any[] = Array.isArray((question as any)?.sub_questions)
      ? (question as any).sub_questions
      : [];
    if (subs.length === 0) return;

    const mapped = subs.map((sq: any) => {
      const options: string[] = Array.isArray(sq?.question_options)
        ? sq.question_options.map((o: any) => o?.data ?? "")
        : [];
      // For MCQ/TF, answers are comma-separated indices like "1, 3"
      const parsedSelected =
        typeof sq?.answer_text === "string"
          ? sq.answer_text
            .split(",")
            .map((s: string) => parseInt(s.trim(), 10))
            .filter((n: number) => !isNaN(n))
          : [];

      return {
        questionHtml: sq?.question_text || "",
        answerHtml: sq?.answer_text || "",
        optionEditors: options,
        selectedAnswers: parsedSelected,
        marks: sq?.max_marks ?? null,
        type: sq?.type || question?.type || "",
        // Preserve id so update payload can assign question_id
        question_id: sq?.question_id || sq?.id,
        id: sq?.id,
      };
    });

    setSubMarkBlocks(mapped);
    // Ensure we don't show extra-question blocks when sub-marks are present
    setExtraQuestionBlocks([]);
  }, [isUpdating, question]);

  const handleAddAnotherQuestion = () => {
    setExtraQuestionBlocks((prev) => [...prev, createEmptyQuestionBlock()]);
  };

  const totalAllocatedSubMarks = () =>
    subMarkBlocks.reduce((sum, b) => sum + (Number(b.marks) || 0), 0);

  const remainingSubMarks = () => {
    const max = Number(validState?.max_marks) || 0;
    return Math.max(0, max - totalAllocatedSubMarks());
  };

  const handleAddSubMarkQuestion = () => {
    if (!validState?.max_marks) {
      setSubMarkError("Please select total Marks first.");
      return;
    }
    if (remainingSubMarks() <= 0) {
      setSubMarkError("No marks remaining to allocate.");
      return;
    }
    setSubMarkError("");
    setSubMarkBlocks((prev) => [...prev, createEmptySubMarkBlock()]);
  };

  const handleAddQuestionAfter = (blockIndex: number) => {
    setExtraQuestionBlocks((prev) => {
      const copy = [...prev];
      copy.splice(blockIndex + 1, 0, createEmptyQuestionBlock());
      return copy;
    });
  };

  // option Editor logic here //
  const handleOptionEditorChange = (index, newOptionData) => {
    setOptionEditors((prevOptions) => {
      const updatedOptions = [...prevOptions];
      updatedOptions[index] = newOptionData;
      return updatedOptions;
    });
  };

  // Editor remove logic here //
  const handleRemoveEditor = (index) => {
    setOptionEditors((prevOptions) =>
      prevOptions.filter((_, i) => i !== index)
    );
  };

  const handleExtraQuestionChange = (blockIndex: number, html: string) => {
    setExtraQuestionBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].questionHtml = html;
      return copy;
    });
  };

  const handleExtraAnswerChange = (blockIndex: number, html: string) => {
    setExtraQuestionBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].answerHtml = html;
      return copy;
    });
  };

  const handleSubMarkQuestionChange = (blockIndex: number, html: string) => {
    setSubMarkBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].questionHtml = html;
      return copy;
    });
  };

  const handleSubMarkAnswerChange = (blockIndex: number, html: string) => {
    setSubMarkBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].answerHtml = html;
      return copy;
    });
  };

  const handleSubMarkAddOption = (blockIndex: number) => {
    setSubMarkBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].optionEditors = [...copy[blockIndex].optionEditors, ""];
      return copy;
    });
  };

  const handleSubMarkRemoveOption = (
    blockIndex: number,
    optionIndex: number
  ) => {
    setSubMarkBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].optionEditors = copy[blockIndex].optionEditors.filter(
        (_, i) => i !== optionIndex
      );
      copy[blockIndex].selectedAnswers = copy[
        blockIndex
      ].selectedAnswers.filter((id) => id !== optionIndex + 1);
      return copy;
    });
  };

  const handleSubMarkOptionChange = (
    blockIndex: number,
    optionIndex: number,
    html: string
  ) => {
    setSubMarkBlocks((prev) => {
      const copy = [...prev];
      const options = [...copy[blockIndex].optionEditors];
      options[optionIndex] = html;
      copy[blockIndex].optionEditors = options;
      return copy;
    });
  };

  const handleSubMarkCheckboxChange = (
    blockIndex: number,
    optionIndex: number
  ) => {
    setSubMarkBlocks((prev) => {
      const copy = [...prev];
      const answers = [...copy[blockIndex].selectedAnswers];
      if (answers.includes(optionIndex + 1)) {
        copy[blockIndex].selectedAnswers = answers.filter(
          (id) => id !== optionIndex + 1
        );
      } else {
        copy[blockIndex].selectedAnswers = [...answers, optionIndex + 1];
      }
      return copy;
    });
  };

  const handleRemoveSubMarkQuestion = (blockIndex: number) => {
    setSubMarkBlocks((prev) => prev.filter((_, i) => i !== blockIndex));
  };

  const handleSubMarkValueChange = (
    blockIndex: number,
    val: { name: string }
  ) => {
    const selected = val ? Number(val.name) : null;
    const currentAllocated = totalAllocatedSubMarks();
    const prevValue = Number(subMarkBlocks[blockIndex]?.marks) || 0;
    const newAllocated = currentAllocated - prevValue + (selected || 0);
    const max = Number(validState?.max_marks) || 0;

    if (newAllocated > max) {
      setSubMarkError(`Total Question Paper Marks: ${max} Total Currently Allocated Mark: ${newAllocated} .`);
      return;
    }
    setSubMarkError("");
    setSubMarkBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].marks = selected;
      return copy;
    });
  };

  const handleSubMarkTypeChange = (
    blockIndex: number,
    val: { name: string }
  ) => {
    setSubMarkBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].type = val?.name || "";
      return copy;
    });
  };

  const handleExtraAddOption = (blockIndex: number) => {
    setExtraQuestionBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].optionEditors = [...copy[blockIndex].optionEditors, ""];
      return copy;
    });
  };

  const handleRemoveExtraQuestion = (blockIndex: number) => {
    setExtraQuestionBlocks((prev) => prev.filter((_, i) => i !== blockIndex));
  };

  const handleExtraOptionChange = (
    blockIndex: number,
    optionIndex: number,
    html: string
  ) => {
    setExtraQuestionBlocks((prev) => {
      const copy = [...prev];
      const options = [...copy[blockIndex].optionEditors];
      options[optionIndex] = html;
      copy[blockIndex].optionEditors = options;
      return copy;
    });
  };

  const handleExtraRemoveOption = (blockIndex: number, optionIndex: number) => {
    setExtraQuestionBlocks((prev) => {
      const copy = [...prev];
      copy[blockIndex].optionEditors = copy[blockIndex].optionEditors.filter(
        (_, i) => i !== optionIndex
      );
      copy[blockIndex].selectedAnswers = copy[
        blockIndex
      ].selectedAnswers.filter((id) => id !== optionIndex + 1);
      return copy;
    });
  };

  const handleExtraCheckboxChange = (
    blockIndex: number,
    optionIndex: number
  ) => {
    setExtraQuestionBlocks((prev) => {
      const copy = [...prev];
      const answers = [...copy[blockIndex].selectedAnswers];
      if (answers.includes(optionIndex + 1)) {
        copy[blockIndex].selectedAnswers = answers.filter(
          (id) => id !== optionIndex + 1
        );
      } else {
        copy[blockIndex].selectedAnswers = [...answers, optionIndex + 1];
      }
      return copy;
    });
  };

  // Questions Manage based on Types //
  useEffect(() => {
    if (location.state && location.state?.question) {
      const questionData = location.state?.question;
      setEditorQuestionHtml(questionData?.question_text || "");
      setEditorAnswerHtml(questionData?.answer_text || "");

      if (
        questionData.type === "Multiple-choice" ||
        (questionData.type === "True / False" && questionData?.question_options)
      ) {
        const options = questionData?.question_options?.map(
          (option) => option?.data || ""
        );
        setOptionEditors(options);

        const answers = questionData?.answer_text
          ? questionData?.answer_text
            ?.split(", ")
            ?.map((id) => parseInt(id, 10))
          : [];
        setSelectedAnswers(answers);
      }
    }
  }, [location.state]);

  // handle Check Duplicacy //

  const similarQuestionList = useTypedSelector(selectSimilarQuestionDataList);
  const loaded = useSelector(
    (state: RootState) => state.similarQuestionState.loaded
  );

  const handleCheckDuplicacy = async () => {
    const errors = await formik.validateForm();

    formik.setTouched({
      question_text: true,
      grade_id: true,
      subject_id: true,
      type: true,
      level: true,
      connected_with: true,
      chapter_id: true,
      topic_id: true,
      max_marks: true,
      concept_level: true,
      skill: true,
    });

    // Validate sub-question marks before checking duplicacy
    if (subMarkBlocks.length > 0) {
      const totalSubMarks = subMarkBlocks.reduce((sum, b) => sum + (Number(b.marks) || 0), 0);
      const maxMarks = Number(formik.values.max_marks) || 0;

      if ((totalSubMarks > maxMarks) || (totalSubMarks < maxMarks)) {
        setSubMarkError(
          `Cannot check duplicacy.Question Max Marks: (${maxMarks}) filled total Mark: (${totalSubMarks}).`
        );
        toastError(
          `Cannot proceed.Question Max Marks: (${maxMarks}) filled total Mark: (${totalSubMarks}).`
        );
        return;
      }
    }

    // If there are no errors, proceed with the API call //
    if (Object.keys(errors).length === 0) {
      const payload = {
        grade_id: formik.values.grade_id[0],
        subject_id: formik.values.subject_id[0],
        topic_id: formik.values.topic_id[0],
        chapter_id: formik.values.chapter_id[0],
        question_text: formik.values.question_text,
      };

      dispatch(similarQuestionAsync(payload));
      setShow(true);
    } else {
      console.log("Form has validation errors. API call is prevented.");
    }
  };

  const stripPTags = (html) => {
    if (!html) return "";

    let temp = document.createElement("div");
    temp.innerHTML = html;

    return temp.textContent || temp.innerText || "";
  };

  // Clear subject_id after new subject list loads (after class changed)
  useEffect(() => {
    if (pendingReset.subject && subjectDataList.length > 0) {
      formik.setFieldValue("subject_id", []);
      formik.setFieldValue("chapter_id", []);
      formik.setFieldValue("topic_id", []);
      setPendingReset((p) => ({ ...p, subject: false }));
    }
  }, [pendingReset.subject, subjectDataList]);

  // Clear chapter_id after new chapter list loads (after subject changed)
  useEffect(() => {
    if (pendingReset.chapter && chaptersDataList.length > 0) {
      formik.setFieldValue("chapter_id", []);
      formik.setFieldValue("topic_id", []);
      setPendingReset((p) => ({ ...p, chapter: false }));
    }
  }, [pendingReset.chapter, chaptersDataList]);

  // Clear topic_id after new topic list loads (after chapter changed)
  useEffect(() => {
    if (pendingReset.topic && TopicDataList.length > 0) {
      formik.setFieldValue("topic_id", []);
      setPendingReset((p) => ({ ...p, topic: false }));
    }
  }, [pendingReset.topic, TopicDataList]);

  // Track if we've loaded the data for updating
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (isUpdating && question && classesDataList.length > 0 && !dataLoadedRef.current) {
      const loadData = async () => {
        setIsLoadingUpdateData(true);
        try {
          // Step 1: Find classId from question data
          const classId = classesDataList.find(
            (cls) => cls.title === question?.grades?.[0]
          )?.id;

          if (classId) {
            // Update formik grade_id if not already set
            if (!formik.values.grade_id[0] || formik.values.grade_id[0] !== classId) {
              formik.setFieldValue("grade_id", [classId]);
            }

            // Step 2: Fetch subjects for this class
            const subjectResponse = await dispatch(
              SubjectListAsync({ gradeId: [classId] })
            );
            const subjects = subjectResponse.payload?.subjects || [];

            // Step 3: Find subjectId from fetched subjects
            const subjectId = subjects.find(
              (subj) => subj.title === question?.subjects?.[0]
            )?.id;

            if (subjectId) {
              // Update formik subject_id if not already set
              if (!formik.values.subject_id[0] || formik.values.subject_id[0] !== subjectId) {
                formik.setFieldValue("subject_id", [subjectId]);
              }

              // Step 4: Fetch chapters for this class and subject
              const chapterResponse = await dispatch(
                ChapterListAsync({ gradeId: [classId], subjectId: [subjectId] })
              );
              const chapters = chapterResponse.payload?.chapters || [];

              // Step 5: Find chapterId from fetched chapters
              // Handle both string and array cases for chapters
              const chapterName = Array.isArray(question?.chapters)
                ? question?.chapters?.[0]
                : question?.chapters;
              const chapterId = chapters.find(
                (ch) => ch.chapter === chapterName
              )?.id;

              if (chapterId) {
                // Update formik chapter_id
                if (!formik.values.chapter_id[0] || formik.values.chapter_id[0] !== chapterId) {
                  formik.setFieldValue("chapter_id", [chapterId]);
                }

                // Step 6: Fetch topics for this class, subject, and chapter
                const topicResponse = await dispatch(
                  TopicListAsync({
                    chapterId: [chapterId],
                    gradeId: [classId],
                    subjectId: [subjectId],
                  })
                );
                const topics = topicResponse.payload?.topics || [];

                // Step 7: Find topicId from fetched topics
                const topicName = Array.isArray(question?.topics)
                  ? question?.topics?.[0]
                  : question?.topics;
                const topicId = topics.find(
                  (top) => top.title === topicName
                )?.id;

                if (topicId) {
                  // Update formik topic_id
                  if (!formik.values.topic_id[0] || formik.values.topic_id[0] !== topicId) {
                    formik.setFieldValue("topic_id", [topicId]);
                  }
                }
              }
            }
          }
          dataLoadedRef.current = true;
        } catch (error) {
          console.error("Error loading update data:", error);
        } finally {
          setIsLoadingUpdateData(false);
        }
      };

      loadData();
    }
  }, [isUpdating, question, classesDataList, dispatch]);

  // Reset the ref and loading state when not updating
  useEffect(() => {
    if (!isUpdating) {
      dataLoadedRef.current = false;
      setIsLoadingUpdateData(false);
    }
  }, [isUpdating]);

  // Update formik values when lists are populated (fallback for when lists load before main useEffect)
  useEffect(() => {
    if (
      isUpdating &&
      question &&
      chaptersDataList.length > 0 &&
      formik.values.grade_id[0] &&
      formik.values.subject_id[0] &&
      !formik.values.chapter_id[0]
    ) {
      const chapterName = Array.isArray(question?.chapters)
        ? question?.chapters?.[0]
        : question?.chapters;
      const chapterId = chaptersDataList.find(
        (ch) => ch.chapter === chapterName
      )?.id;
      if (chapterId) {
        formik.setFieldValue("chapter_id", [chapterId]);
      }
    }
  }, [isUpdating, question, chaptersDataList, formik.values.grade_id, formik.values.subject_id]);

  useEffect(() => {
    if (
      isUpdating &&
      question &&
      TopicDataList.length > 0 &&
      formik.values.chapter_id[0] &&
      !formik.values.topic_id[0]
    ) {
      const topicName = Array.isArray(question?.topics)
        ? question?.topics?.[0]
        : question?.topics;
      const topicId = TopicDataList.find(
        (top) => top.title === topicName
      )?.id;
      if (topicId) {
        formik.setFieldValue("topic_id", [topicId]);
      }
    }
  }, [isUpdating, question, TopicDataList, formik.values.chapter_id]);

  // tsx here //
  return (
    <>
      {/* Add or Edit Question page here */}
      <div className="container">
        <div className="inner-section d-flex gap-1 flex-wrap items-center justify-content-between">
          <div className="left-area">
            <h6>{isUpdating ? "Update Question" : "Add New Question"}</h6>
          </div>
          <div className="right-area">
            <button
              className="custom-inactive-button rounded-lg"
              onClick={handleCancelBtn}
            >
              Cancel
            </button>
            <button
              className="w-full custom-active-button rounded-lg"
              onClick={handleCheckDuplicacy}
            >
              Check Duplicacy
            </button>
          </div>
        </div>
      </div>

      {/* create Question Page here  */}
      <div className="container" style={{ position: "relative" }}>
        {
          isLoadingUpdateData ? (
            <Loader />
          ) :
            <>
              <form
                action=""
                className="horizaontal-form question-form"
                onSubmit={formik.handleSubmit}
                style={{ opacity: isLoadingUpdateData ? 0.5 : 1 }}
              >
                <div className="col-md-12">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group mb-4">
                        <label htmlFor="">Class</label>
                        <span className="form_required">*</span>
                        <Listbox
                          value={formik.values.grade_id[0] || ""}
                          onChange={async (value) => {
                            await formik.setFieldValue("grade_id", [Number(value)]);
                            dispatch(SubjectListAsync({ gradeId: [Number(value)] }));
                            setPendingReset({
                              subject: true,
                              chapter: false,
                              topic: false,
                            });
                          }}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left w-full">
                              <span className="block truncate">
                                {selectedClass?.title || "Select Class"}
                                {/* {classesDataList?.find(item => item.id === formik.values.grade_id[0])?.title || "Select Class"} */}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {classesDataList?.length > 0 ? (
                                  classesDataList?.map((classItem, index) => (
                                    <Listbox.Option
                                      key={index}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                          ? "bg-orange-100 text-orange-700"
                                          : "text-gray-900"
                                        }`
                                      }
                                      value={classItem?.id}
                                    >
                                      {({ selected }) => (
                                        <>
                                          <span
                                            className={`block truncate ${selected ? "font-medium" : "font-normal"
                                              }`}
                                          >
                                            {classItem?.title}
                                          </span>
                                          {selected && (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                              <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  ))
                                ) : (
                                  <div className="relative cursor-default select-none py-2 pl-10 pr-4 text-gray-700">
                                    No Values Found
                                  </div>
                                )}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                        {formik.errors.grade_id && formik.touched.grade_id && (
                          <div className="error">{formik.errors.grade_id}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group mb-4">
                        <label htmlFor="subject-select">
                          Subject<span className="form_required">*</span>
                        </label>
                        <Listbox
                          value={formik.values.subject_id[0] || ""}
                          onChange={async (value) => {
                            await formik.setFieldValue("subject_id", [Number(value)]);
                            dispatch(
                              ChapterListAsync({
                                subjectId: [Number(value)],
                                gradeId: [Number(formik.values.grade_id[0])],
                              })
                            );
                            setPendingReset({
                              ...pendingReset,
                              chapter: true,
                              topic: false,
                            }); // clear chapter/topic when new chapter list arrives
                          }}
                          disabled={!formik.values.grade_id[0]}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative w-full text-left">
                              <span className="block truncate">
                                {selectedSubject?.title || "Select Subject"}
                                {/* {subjectDataList?.find(subject => subject.id === formik.values.subject_id[0])?.title || "Select Subject"} */}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {subjectDataList?.length > 0 ? (
                                  subjectDataList?.map((subject, index) => (
                                    <Listbox.Option
                                      key={index}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                          ? "bg-orange-100 text-orange-700"
                                          : "text-gray-900"
                                        }`
                                      }
                                      value={subject?.id}
                                    >
                                      {({ selected }) => (
                                        <>
                                          <span
                                            className={`block truncate ${selected ? "font-medium" : "font-normal"
                                              }`}
                                          >
                                            {subject?.title}
                                          </span>
                                          {selected && (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                              <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  ))
                                ) : (
                                  <div className="relative cursor-default select-none py-2 pl-10 pr-4 text-gray-700">
                                    No Values Found
                                  </div>
                                )}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                        {errors.subject_id && touched.subject_id && (
                          <div className="error">{errors.subject_id}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="">
                          Type<span className="form_required">*</span>
                        </label>
                        <Listbox
                          value={validState?.type}
                          onChange={(val) => handleFieldChange("type", val)}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left">
                              <span className="block truncate">
                                {validState?.type || "Select type"}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {questionType?.map((type, typeIdx) => (
                                  <Listbox.Option
                                    key={typeIdx}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                        ? "bg-orange-100 text-orange-700"
                                        : "text-gray-900"
                                      }`
                                    }
                                    value={type}
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span
                                          className={`block truncate ${selected ? "font-medium" : "font-normal"
                                            }`}
                                        >
                                          {type?.name}
                                        </span>
                                        {selected ? (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                            <CheckIcon
                                              className="h-5 w-5"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        ) : null}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                        {formik.touched.type && formik.errors.type && (
                          <div className="error">{formik.errors.type}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="">
                          Difficulty Level<span className="form_required">*</span>
                        </label>
                        <Listbox
                          value={validState?.level}
                          onChange={(val) => handleFieldChange("level", val)}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left">
                              <span className="block truncate">
                                {validState?.level || "Select level"}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {questionLevel?.map((level, levelIdx) => (
                                  <Listbox.Option
                                    key={levelIdx}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                        ? "bg-orange-100 text-orange-700"
                                        : "text-gray-900"
                                      }`
                                    }
                                    value={level}
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span
                                          className={`block truncate ${selected ? "font-medium" : "font-normal"
                                            }`}
                                        >
                                          {level?.name}
                                        </span>
                                        {selected ? (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                            <CheckIcon
                                              className="h-5 w-5"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        ) : null}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                        {formik.touched.level && formik.errors.level && (
                          <div className="error">{formik.errors.level}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="">Connected With</label>
                        <span className="form_required">*</span>
                        <Listbox
                          value={validState?.connected_with}
                          onChange={(val) => {
                            const selectedClassId = classesDataList?.find(
                              (item) => item?.title === values?.grade_id[0]
                            )?.id;
                            if (selectedClassId) {
                              setGradeId([selectedClassId]);
                            }
                            dispatch(
                              ChapterListAsync({
                                gradeId: values?.grade_id ?? [],
                                subjectId: values?.subject_id ?? [],
                              })
                            );
                            handleFieldChange("connected_with", val);
                          }}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left">
                              <span className="block truncate">
                                {validState?.connected_with ||
                                  "Select connected with"}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {connectedWith?.map(
                                  (ConnectedWith, ConnectedWithIdx) => (
                                    <Listbox.Option
                                      key={ConnectedWithIdx}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                          ? "bg-orange-100 text-orange-700"
                                          : "text-gray-900"
                                        }`
                                      }
                                      value={ConnectedWith}
                                    >
                                      {({ selected }) => (
                                        <>
                                          <span
                                            className={`block truncate ${selected ? "font-medium" : "font-normal"
                                              }`}
                                          >
                                            {ConnectedWith?.name}
                                          </span>
                                          {selected ? (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                              <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  )
                                )}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                        {formik.errors.connected_with &&
                          formik.touched.connected_with && (
                            <div className="error">
                              {formik.errors.connected_with}
                            </div>
                          )}
                      </div>
                    </div>

                    {(validState?.connected_with === "Chapters" ||
                      validState?.connected_with === "Topics") && (
                        <>
                          {/* Chapter Field */}
                          <div className="col-md-4">
                            <div className="form-group mb-4">
                              <label htmlFor="chapterSelect">Chapter</label>
                              <span className="form_required">*</span>
                              <Listbox
                                value={formik.values.chapter_id[0] || ""}
                                onChange={async (value) => {
                                  await formik.setFieldValue("chapter_id", [
                                    Number(value),
                                  ]);
                                  dispatch(
                                    TopicListAsync({
                                      chapterId: [Number(value)],
                                      gradeId: [Number(formik.values.grade_id[0])],
                                      subjectId: [Number(formik.values.subject_id[0])],
                                    })
                                  );
                                  setPendingReset({ ...pendingReset, topic: true });
                                }}
                                disabled={
                                  !(formik.values.grade_id && formik.values.subject_id)
                                }
                              >
                                <div className="relative">
                                  <Listbox.Button className="relative w-full text-left">
                                    <span className="block truncate">
                                      {selectedChapter?.chapter || "Select Chapter"}
                                      {/* {chaptersDataList?.find(chapter => chapter.id === formik.values.chapter_id[0])?.chapter || "Select Chapter"} */}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                      <ChevronUpDownIcon
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  </Listbox.Button>
                                  <Transition
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                  >
                                    <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                      {chaptersDataList?.length > 0 ? (
                                        chaptersDataList?.map((chapter, idx) => (
                                          <Listbox.Option
                                            key={idx}
                                            className={({ active }) =>
                                              `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                ? "bg-orange-100 text-orange-700"
                                                : "text-gray-900"
                                              }`
                                            }
                                            value={chapter.id}
                                          >
                                            {({ selected }) => (
                                              <span
                                                className={`block truncate ${selected
                                                  ? "font-medium"
                                                  : "font-normal"
                                                  }`}
                                              >
                                                {chapter?.chapter}
                                                {selected && (
                                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                    <CheckIcon
                                                      className="h-5 w-5"
                                                      aria-hidden="true"
                                                    />
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                          </Listbox.Option>
                                        ))
                                      ) : (
                                        <div className="relative cursor-default select-none py-2 pl-10 pr-4 text-gray-700">
                                          No Values Found
                                        </div>
                                      )}
                                    </Listbox.Options>
                                  </Transition>
                                </div>
                              </Listbox>
                              {formik.errors.chapter_id &&
                                formik.touched.chapter_id && (
                                  <div className="error">
                                    {formik.errors.chapter_id}
                                  </div>
                                )}
                            </div>
                          </div>

                          {/* Topic Field */}
                          <div className="col-md-4">
                            <div className="form-group mb-4">
                              <label htmlFor="topicSelect">Topic</label>
                              <span className="form_required">*</span>
                              <Listbox
                                value={formik.values.topic_id[0] || ""}
                                onChange={async (value) => {
                                  await formik.setFieldValue("topic_id", [
                                    Number(value),
                                  ]);
                                }}
                                disabled={!formik.values.chapter_id}
                              >
                                <div className="relative">
                                  <Listbox.Button className="relative w-full text-left">
                                    <span className="block truncate">
                                      {selectedTopic?.title || "Select Topic"}
                                      {/* {TopicDataList?.find(topic => topic.id === formik.values.topic_id[0])?.title || "Select Topic"} */}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                      <ChevronUpDownIcon
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  </Listbox.Button>
                                  <Transition
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                  >
                                    <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                      {TopicDataList?.length > 0 ? (
                                        TopicDataList?.map((TopicItem, index) => (
                                          <Listbox.Option
                                            key={index}
                                            className={({ active }) =>
                                              `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                                ? "bg-orange-100 text-orange-700"
                                                : "text-gray-900"
                                              }`
                                            }
                                            value={TopicItem?.id}
                                            disabled={!formik.values.chapter_id}
                                          >
                                            {({ selected }) => (
                                              <span
                                                className={`block truncate ${selected
                                                  ? "font-medium"
                                                  : "font-normal"
                                                  }`}
                                              >
                                                {TopicItem?.title}
                                                {selected && (
                                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                    <CheckIcon
                                                      className="h-5 w-5"
                                                      aria-hidden="true"
                                                    />
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                          </Listbox.Option>
                                        ))
                                      ) : (
                                        <div className="relative cursor-default select-none py-2 pl-10 pr-4 text-gray-700">
                                          No Values Found
                                        </div>
                                      )}
                                    </Listbox.Options>
                                  </Transition>
                                </div>
                              </Listbox>
                              {formik.errors.topic_id && formik.touched.topic_id && (
                                <div className="error">{formik.errors.topic_id}</div>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="">Based On</label>
                        <Listbox
                          value={validState?.based_on}
                          onChange={(val) => handleFieldChange("based_on", val)}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left">
                              <span className="block truncate">
                                {validState?.based_on || "-- None --"}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {BasedOn?.map((BasedOn, BasedOnIdx) => (
                                  <Listbox.Option
                                    key={BasedOnIdx}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                        ? "bg-orange-100 text-orange-700"
                                        : "text-gray-900"
                                      }`
                                    }
                                    value={BasedOn}
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span
                                          className={`block truncate ${selected ? "font-medium" : "font-normal"
                                            }`}
                                        >
                                          {BasedOn?.name}
                                        </span>
                                        {selected ? (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                            <CheckIcon
                                              className="h-5 w-5"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        ) : null}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="">Concept Level</label>
                        <span className="form_required">*</span>
                        <Listbox
                          value={validState?.concept_level}
                          onChange={(val) => handleFieldChange("concept_level", val)}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left">
                              <span className="block truncate">
                                {validState?.concept_level || "Select concept level"}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {ConceptLevel?.map(
                                  (ConceptLevel, ConceptLevelIdx) => (
                                    <Listbox.Option
                                      key={ConceptLevelIdx}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                          ? "bg-orange-100 text-orange-700"
                                          : "text-gray-900"
                                        }`
                                      }
                                      value={ConceptLevel}
                                    >
                                      {({ selected }) => (
                                        <>
                                          <span
                                            className={`block truncate ${selected ? "font-medium" : "font-normal"
                                              }`}
                                          >
                                            {ConceptLevel?.name}
                                          </span>
                                          {selected ? (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                              <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  )
                                )}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                        {formik.touched.concept_level &&
                          formik.errors.concept_level && (
                            <div className="error">{formik.errors.concept_level}</div>
                          )}
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="competency">Competency / Skill Level</label>
                        <span className="form_required">*</span>
                        <Listbox
                          value={validState?.skill}
                          onChange={(val) => handleFieldChange("skill", val)}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left">
                              <span className="block truncate">
                                {validState?.skill || "Select skill"}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {skills?.map((skill, skillIdx) => (
                                  <Listbox.Option
                                    key={skillIdx}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                        ? "bg-orange-100 text-orange-700"
                                        : "text-gray-900"
                                      }`
                                    }
                                    value={skill}
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span
                                          className={`block truncate ${selected ? "font-medium" : "font-normal"
                                            }`}
                                        >
                                          {skill?.name}
                                        </span>
                                        {selected ? (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                            <CheckIcon
                                              className="h-5 w-5"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        ) : null}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                        {formik.touched.skill && formik.errors.skill && (
                          <div className="error">{formik.errors.skill}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="">
                          Marks<span className="form_required">*</span>
                        </label>
                        <Listbox
                          value={validState?.max_marks}
                          onChange={(val) => handleFieldChange("max_marks", val)}
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left">
                              <span className="block truncate">
                                {validState?.max_marks || "Select marks"}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {questionMarks?.map((mark, markIdx) => (
                                  <Listbox.Option
                                    key={markIdx}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                        ? "bg-orange-100 text-orange-700"
                                        : "text-gray-900"
                                      }`
                                    }
                                    value={mark}
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span
                                          className={`block truncate ${selected ? "font-medium" : "font-normal"
                                            }`}
                                        >
                                          {mark?.name}
                                        </span>
                                        {selected ? (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                            <CheckIcon
                                              className="h-5 w-5"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        ) : null}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                        {formik.touched.max_marks && formik.errors.max_marks && (
                          <div className="error">{formik.errors.max_marks}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="occurringHistory">Occurring History</label>
                        <Listbox
                          value={validState?.occuring_history}
                          onChange={(val) =>
                            handleFieldChange("occuring_history", val)
                          }
                        >
                          <div className="relative">
                            <Listbox.Button className="relative text-left">
                              <span className="block truncate">
                                {validState?.occuring_history ||
                                  "-- None --"}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                {occurringHistory?.map(
                                  (occurringHistory, occurringHistoryIdx) => (
                                    <Listbox.Option
                                      key={occurringHistoryIdx}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                          ? "bg-orange-100 text-orange-700"
                                          : "text-gray-900"
                                        }`
                                      }
                                      value={occurringHistory}
                                    >
                                      {({ selected }) => (
                                        <>
                                          <span
                                            className={`block truncate ${selected ? "font-medium" : "font-normal"
                                              }`}
                                          >
                                            {occurringHistory?.name}
                                          </span>
                                          {selected ? (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                              <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  )
                                )}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>
                    </div>

                    <div className="col-md-4 mb-4">
                      <div className="form-group">
                        <label htmlFor="">Reference By</label>
                        <span className="form_required"></span>
                        <input
                          id="reference_by"
                          type="text"
                          name="reference_by"
                          className="form-control"
                          placeholder="Enter reference"
                          onChange={(e) => handleFieldChange("reference_by", e.target.value)}
                        />
                        {/* {formik.touched.reference_by && formik.errors.reference_by && (
                    <div className="error">{formik.errors.reference_by}</div>
                  )} */}
                      </div>
                    </div>

                    {validState.occuring_history === "Board Exams" && (
                      <div className="col-md-4 mb-4">
                        <div className="form-group">
                          <label htmlFor="board_exam">Board Exams</label>
                          <Listbox
                            value={validState?.board_exam}
                            onChange={(val) => handleFieldChange("board_exam", val)}
                          >
                            <div className="relative">
                              <Listbox.Button className="relative text-left">
                                <span className="block truncate">
                                  {validState?.board_exam || "-- None --"}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                </span>
                              </Listbox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                  {BoardExams?.map((boardExam, index) => (
                                    <Listbox.Option
                                      key={index}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                          ? "bg-orange-100 text-orange-700"
                                          : "text-gray-900"
                                        }`
                                      }
                                      value={boardExam}
                                    >
                                      {({ selected }) => (
                                        <>
                                          <span
                                            className={`block truncate ${selected ? "font-medium" : "font-normal"
                                              }`}
                                          >
                                            {boardExam?.name}
                                          </span>
                                          {selected && (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                              <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  ))}
                                </Listbox.Options>
                              </Transition>
                            </div>
                          </Listbox>
                        </div>
                      </div>
                    )}
                    {(validState?.board_exam == "CBSE" ||
                      validState?.board_exam == "ICSE") &&
                      validState?.occuring_history !== "Competitive Exams" && (
                        <div className="col-md-4 mb-4">
                          <div className="form-group">
                            <label htmlFor="board_exam_year">
                              {`${validState?.board_exam} Exam Year`}
                            </label>
                            <Listbox
                              value={validState?.board_exam_year}
                              onChange={(val) =>
                                handleFieldChange("board_exam_year", val)
                              }
                            >
                              <div className="relative mt-1">
                                <Listbox.Button className="relative text-left">
                                  <span className="block truncate">
                                    {validState?.board_exam_year ||
                                      "-- None --"}
                                  </span>
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon
                                      className="h-5 w-5 text-gray-400"
                                      aria-hidden="true"
                                    />
                                  </span>
                                </Listbox.Button>
                                <Transition
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                    {BoardExamsYear?.map((examYear, index) => (
                                      <Listbox.Option
                                        key={index}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                            ? "bg-orange-100 text-orange-700"
                                            : "text-gray-900"
                                          }`
                                        }
                                        value={examYear}
                                      >
                                        {({ selected }) => (
                                          <>
                                            <span
                                              className={`block truncate ${selected
                                                ? "font-medium"
                                                : "font-normal"
                                                }`}
                                            >
                                              {examYear?.name}
                                            </span>
                                            {selected && (
                                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                <CheckIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </Listbox>
                          </div>
                        </div>
                      )}

                    {validState?.occuring_history === "Competitive Exams" && (
                      <div className="col-md-4 mb-4">
                        <div className="form-group">
                          <label htmlFor="">Competitive Exam Name</label>
                          <input
                            id="competative_exam"
                            type="text"
                            className="form-control"
                            placeholder="Enter competitive exam name"
                            value={values?.competative_exam}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    )}

                    {validState?.occuring_history === "Competitive Exams" && (
                      <div className="col-md-4 mb-4">
                        <div className="form-group">
                          <label htmlFor="competative_exam_year">
                            Competitive Exam Year
                          </label>
                          <Listbox
                            value={validState?.competative_exam_year}
                            onChange={(val) =>
                              handleFieldChange("competative_exam_year", val)
                            }
                          >
                            <div className="relative">
                              <Listbox.Button className="relative text-left">
                                <span className="block truncate">
                                  {validState?.competative_exam_year ||
                                    "-- None --"}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                </span>
                              </Listbox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                  {CompetitiveExamsYear?.map(
                                    (
                                      CompetitiveExamsYear,
                                      CompetitiveExamsYearindex
                                    ) => (
                                      <Listbox.Option
                                        key={CompetitiveExamsYearindex}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                            ? "bg-orange-100 text-orange-700"
                                            : "text-gray-900"
                                          }`
                                        }
                                        value={CompetitiveExamsYear}
                                      >
                                        {({ selected }) => (
                                          <>
                                            <span
                                              className={`block truncate ${selected
                                                ? "font-medium"
                                                : "font-normal"
                                                }`}
                                            >
                                              {CompetitiveExamsYear?.name}
                                            </span>
                                            {selected && (
                                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                <CheckIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    )
                                  )}
                                </Listbox.Options>
                              </Transition>
                            </div>
                          </Listbox>
                        </div>
                      </div>
                    )}

                    {/*  Status field show only in Edit mode */}
                    {isUpdating && (
                      <div className="form-group col-md-4 mb-4">
                        <label htmlFor="">Status</label>
                        <Form>
                          <Form.Check
                            type="switch"
                            id="custom-switch"
                            label={!!statusInfo ? "Activated" : "Deactivated"}
                            checked={statusInfo}
                            onChange={handleStatusChangeInUser}
                          />
                        </Form>
                      </div>
                    )}



                  </div>
                </div>
              </form>

              <div
                className="p-2"
                style={{ border: "1px solid #eee", borderRadius: 6 }}
              >
                {/* Question CKEditor Fields  */}
                <div className="form-group mb-4">
                  <label htmlFor="">
                    Question Text<span className="form_required">*</span>
                  </label>
                  <CKEditor
                    initData={editorQuestionHtml}
                    onChange={handleQuestionEditor}
                  />
                  {formik.touched.question_text && formik.errors.question_text ? (
                    <div className="error">{formik.errors.question_text}</div>
                  ) : null}
                </div>

                {/* Options Editor open when user select MCQ and True/False */}
                {optionEditors?.map((option, index) => (
                  <div key={index} className="col-md-12 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5>Option {index + 1}</h5>
                      <button
                        type="button"
                        className="custom-inactive-button rounded-lg"
                        onClick={() => handleRemoveEditor(index)}
                      >
                        Close
                      </button>
                    </div>

                    {/* Checkbox showing with particular options */}
                    <div>
                      <input
                        type="checkbox"
                        className="theme-checkbox"
                        id={`option-${index}`}
                        checked={selectedAnswers.includes(index + 1)}
                        onChange={() => handleCheckboxChange(index)}
                      />
                      <CKEditor
                        initData={option || ""}
                        onChange={(event) =>
                          handleOptionEditorChange(index, event.editor?.getData())
                        }
                      />
                    </div>
                  </div>
                ))}

                {/* Add options button when user select MCQ and True/False */}
                {formik.values.type === "Multiple-choice" ||
                  formik.values.type === "True / False" ? (
                  <div className="col-md-4 mb-4">
                    <button
                      className="custom-active-button rounded-lg"
                      type="button"
                      onClick={handleOpenEditor}
                    >
                      Add Option
                    </button>
                  </div>
                ) : null}

                {/* Answer Field showing in when user selected  Match Making,Very Short Answer,Short answer,Long Questions*/}
                {formik.values.type === "Match Making" ||
                  formik.values.type === "Very Short Answer" ||
                  formik.values.type === "Short answer" ||
                  formik.values.type === "Paragraph" ||
                  formik.values.type === "Long" ? (
                  <div className="form-group mb-4">
                    <label>Answer Text</label>
                    <CKEditor
                      initData={editorAnswerHtml}
                      onChange={handleAnswerEditor}
                    />
                  </div>
                ) : null}

                {/* Add another question after completing first question's fields */}
                <div
                  className="col-md-4 mb-4"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-around",
                  }}
                >
                  <button
                    type="button"
                    data-tooltip-id="more-question-tip"
                    data-tooltip-content="Click to add more question"
                    className="custom-active-button rounded-lg flex items-center gap-2 px-3 py-2"
                    onClick={handleAddAnotherQuestion}
                    disabled={subMarkBlocks.length > 0}
                  >
                    <Plus size={20} />
                    More Question
                  </button>
                  <Tooltip id="more-question-tip" />

                  <button
                    type="button"
                    data-tooltip-id="submark-tip"
                    data-tooltip-content="Click here to add a sub-mark question"
                    className="custom-active-button rounded-lg flex items-center gap-2 px-3 py-2"
                    onClick={handleAddSubMarkQuestion}
                    disabled={extraQuestionBlocks.length > 0}
                  >
                    <Plus size={20} />
                    <span>SubMark Question</span>
                  </button>
                  <Tooltip id="submark-tip" />

                  {subMarkError && <span className="">{subMarkError}</span>}
                </div>
              </div>

              {extraQuestionBlocks.map((block, bIndex) => (
                <div key={`qblock-${bIndex}`} className="card mt-2 mb-4">
                  <div className="card__header">
                    <div>
                      <strong className="card__title">
                        Additional Question #{bIndex + 2}
                      </strong>
                      <div className="card__subtitle">
                        Add another variation of the current question.
                      </div>
                    </div>
                    <div className="section-actions">
                      {/* <span className="badge badge--muted help-tooltip" data-title="Additional questions are alternative versions that share the same metadata (class, subject, etc.).">Help</span> */}
                      {/* <button
                  type="button"
                  className="custom-inactive-button rounded-lg"
                  onClick={() => handleRemoveExtraQuestion(bIndex)}
                >
                  Remove
                </button> */}

                      <button
                        type="button"
                        className="bg-grey-100 rounded-lg flex items-center gap-2 px-3 py-2"
                        onClick={() => handleRemoveExtraQuestion(bIndex)}
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                  <div className="form-group mb-3">
                    <label>Question Text (#{bIndex + 2})</label>
                    <CKEditor
                      initData={block.questionHtml}
                      onChange={(e) =>
                        handleExtraQuestionChange(bIndex, e.editor?.getData())
                      }
                    />
                  </div>

                  {(formik.values.type === "Multiple-choice" ||
                    formik.values.type === "True / False") && (
                      <>
                        {block.optionEditors.map((opt, oIndex) => (
                          <div
                            key={`q${bIndex}-opt-${oIndex}`}
                            className="col-md-12 mb-3"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6>Option {oIndex + 1}</h6>
                              <button
                                type="button"
                                className="custom-inactive-button rounded-lg"
                                onClick={() => handleExtraRemoveOption(bIndex, oIndex)}
                              >
                                Close
                              </button>
                            </div>
                            <div>
                              <input
                                type="checkbox"
                                className="theme-checkbox"
                                checked={block.selectedAnswers.includes(oIndex + 1)}
                                onChange={() =>
                                  handleExtraCheckboxChange(bIndex, oIndex)
                                }
                              />
                              <CKEditor
                                initData={opt || ""}
                                onChange={(e) =>
                                  handleExtraOptionChange(
                                    bIndex,
                                    oIndex,
                                    e.editor?.getData()
                                  )
                                }
                              />
                            </div>
                          </div>
                        ))}
                        <div className="mb-3">
                          <button
                            type="button"
                            className="custom-active-button rounded-lg"
                            onClick={() => handleExtraAddOption(bIndex)}
                          >
                            Add Option
                          </button>
                        </div>
                      </>
                    )}

                  {(formik.values.type === "Match Making" ||
                    formik.values.type === "Very Short Answer" ||
                    formik.values.type === "Short answer" ||
                    formik.values.type === "Paragraph" ||
                    formik.values.type === "Long") && (
                      <div className="form-group mb-2">
                        <label>Answer Text</label>
                        <CKEditor
                          initData={block.answerHtml}
                          onChange={(e) =>
                            handleExtraAnswerChange(bIndex, e.editor?.getData())
                          }
                        />
                      </div>
                    )}

                  {/* Add another question after this block */}
                  <div className="mb-2 text-end justify-center">
                    <button
                      type="button"
                      className="custom-active-button rounded-lg flex items-center gap-2 px-3 py-2"
                      data-tooltip-id="new-more-question"
                      data-tooltip-content="Click here to add new question"
                      onClick={() => handleAddQuestionAfter(bIndex)}
                      disabled={subMarkBlocks.length > 0}
                    >
                      <Plus />
                      <span>More Question</span>
                    </button>
                    <Tooltip id="new-more-question" />
                  </div>
                </div>
              ))}

              {/* Sub-Mark Questions */}
              {subMarkBlocks.map((block, sIndex) => (
                <>
                  <div
                    key={`submark-${sIndex}`}
                    className="card mt-4 mb-4 border-black p-4 flex flex-col"
                  >
                    <div className="card__header">
                      <div>
                        <strong className="card__title">Sub Question</strong>
                        <div className="card__subtitle">
                          Split total marks into smaller sub-questions.
                        </div>
                      </div>
                      <div className="section-actions">
                        {/* <span className="badge badge--muted help-tooltip" data-title="Use sub-questions to allocate portions of the total marks to multiple prompts.">Help</span> */}

                        <button
                          type="button"
                          className="bg-grey-100 rounded-lg flex items-center gap-2 px-3 py-2"
                          onClick={() => handleRemoveSubMarkQuestion(sIndex)}
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                    <div className="row">
                      {/* Left column - Sub Marks and Sub Question Type stacked */}
                      <div className="col-md-3">
                        <div className="mb-3">
                          <div className="form-group">
                            <label>Sub Marks</label>
                            <Listbox
                              value={block.marks}
                              onChange={(val) => handleSubMarkValueChange(sIndex, val)}
                            >
                              <div className="relative">
                                <Listbox.Button className="relative text-left w-full">
                                  <span className="block truncate">
                                    {block.marks || "Select marks"}
                                  </span>
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon
                                      className="h-5 w-5 text-gray-400"
                                      aria-hidden="true"
                                    />
                                  </span>
                                </Listbox.Button>
                                <Transition
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                    {questionMarks.map((mark, idx) => (
                                      <Listbox.Option
                                        key={idx}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                            ? "bg-orange-100 text-orange-700"
                                            : "text-gray-900"
                                          }`
                                        }
                                        value={mark}
                                      >
                                        {({ selected }) => (
                                          <>
                                            <span
                                              className={`block truncate ${selected ? "font-medium" : "font-normal"
                                                }`}
                                            >
                                              {mark.name}
                                            </span>
                                            {selected && (
                                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                <CheckIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </Listbox>
                            <div className="mt-2">
                              <span className="badge badge--warning !text-blue-400">
                                Remaining marks: {remainingSubMarks()}
                              </span>
                            </div>
                            {subMarkError && (
                              <div className="error mt-2">{subMarkError}</div>
                            )}
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="form-group">
                            <label>Sub Question Type</label>
                            <Listbox
                              value={block.type || formik.values.type}
                              onChange={(val) => handleSubMarkTypeChange(sIndex, val)}
                            >
                              <div className="relative">
                                <Listbox.Button className="relative text-left w-full">
                                  <span className="block truncate">
                                    {block.type || formik.values.type || "Select type"}
                                  </span>
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon
                                      className="h-5 w-5 text-gray-400"
                                      aria-hidden="true"
                                    />
                                  </span>
                                </Listbox.Button>
                                <Transition
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                    {questionType.map((typeItem) => (
                                      <Listbox.Option
                                        key={typeItem.name}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active
                                            ? "bg-orange-100 text-orange-700"
                                            : "text-gray-900"
                                          }`
                                        }
                                        value={typeItem}
                                      >
                                        {({ selected }) => (
                                          <>
                                            <span
                                              className={`block truncate ${selected ? "font-medium" : "font-normal"
                                                }`}
                                            >
                                              {typeItem.name}
                                            </span>
                                            {selected && (
                                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                <CheckIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </Listbox>
                          </div>
                        </div>
                      </div>

                      {/* Right column - Question Text editor and Options/Answers */}
                      <div className="col-md-9">
                        <div className="form-group mb-3">
                          <label>Question Text</label>
                          <CKEditor
                            initData={block.questionHtml}
                            onChange={(e) =>
                              handleSubMarkQuestionChange(sIndex, e.editor?.getData())
                            }
                          />
                        </div>

                        {/* Options/Answers section - aligned with editor */}
                        {((block.type || formik.values.type) === "Multiple-choice" ||
                          (block.type || formik.values.type) === "True / False") && (
                            <>
                              {block.optionEditors?.map((opt, oIndex) => (
                                <div
                                  key={`s${sIndex}-opt-${oIndex}`}
                                  className="mb-3"
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6>Option {oIndex + 1}</h6>
                                    <button
                                      type="button"
                                      className="custom-inactive-button rounded-lg"
                                      onClick={() =>
                                        handleSubMarkRemoveOption(sIndex, oIndex)
                                      }
                                    >
                                      Close
                                    </button>
                                  </div>
                                  <div>
                                    <input
                                      type="checkbox"
                                      className="theme-checkbox"
                                      checked={block.selectedAnswers.includes(
                                        oIndex + 1
                                      )}
                                      onChange={() =>
                                        handleSubMarkCheckboxChange(sIndex, oIndex)
                                      }
                                    />
                                    <CKEditor
                                      initData={opt || ""}
                                      onChange={(e) =>
                                        handleSubMarkOptionChange(
                                          sIndex,
                                          oIndex,
                                          e.editor?.getData()
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              ))}
                              <div className="mb-3 mt-2">
                                <button
                                  type="button"
                                  className="custom-active-button rounded-lg"
                                  onClick={() => handleSubMarkAddOption(sIndex)}
                                >
                                  Add Option
                                </button>
                              </div>
                            </>
                          )}
                        {((block.type || formik.values.type) === "Match Making" ||
                          (block.type || formik.values.type) === "Very Short Answer" ||
                          (block.type || formik.values.type) === "Short answer" ||
                          (block.type || formik.values.type) === "Paragraph" ||
                          (block.type || formik.values.type) === "Long") && (
                            <div className="form-group mb-2">
                              <label>Answer Text</label>
                              <CKEditor
                                initData={block.answerHtml}
                                onChange={(e) =>
                                  handleSubMarkAnswerChange(sIndex, e.editor?.getData())
                                }
                              />
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="flex">
                    <button
                      type="button"
                      className="custom-active-button rounded-lg flex items-center gap-2 px-3 py-2 ml-auto"
                      onClick={handleAddSubMarkQuestion}
                      data-tooltip-id="sub-mark-new-question"
                      data-tooltip-content="Click to add more sub-question"
                    >
                      <Plus />
                      <span>SubMark Question</span>
                    </button>
                  </div>
                  <Tooltip id="sub-mark-new-question" />
                </>
              ))}
            </>
        }
      </div>

      {/* Duplicate Question Modal here */}

      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="title-text">Similar Questions :-</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={loaded ? "" : "modal-content-blur"}>
            <form action="" className="horizontal-form">
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {similarQuestionList?.length > 0 ? (
                  similarQuestionList?.map((question, index) => (
                    <div key={index} className="question-block">
                      <p>
                        <strong>Question {index + 1}:</strong>{" "}
                        {stripPTags(question?.target)}
                      </p>
                    </div>
                  ))
                ) : (
                  <p>No similar questions found.</p>
                )}
              </div>
              <div className="form-group mb-4 d-flex gap-2">
                <button
                  className="w-full custom-active-button rounded-lg"
                  type="button"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="w-full custom-active-button rounded-lg"
                  type="submit"
                  onClick={onSubmit}
                  disabled={!loaded || formik.isSubmitting}
                >
                  {formik.isSubmitting
                    ? isUpdating
                      ? "Updating ..."
                      : "Saving ..."
                    : isUpdating
                      ? "Update "
                      : "Save"}
                </button>
              </div>
            </form>
          </div>

          {!loaded && (
            <div className="full-page-spinner">
              <Spinner animation="border" />
              <div className="spinner-text">
                Finding Duplicate Questions ...
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CreateQuestions;